/**
 * Onboarding Primitives
 *
 * Core building blocks for the Onboarding compound component.
 * Icon, Title, Description, Header, Body, Footer, Content.
 */

import * as React from 'react';
import { cn } from '@newcore/ui/cn';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import type {
  OnboardingBodyProps,
  OnboardingContentProps,
  OnboardingDescriptionProps,
  OnboardingFooterProps,
  OnboardingHeaderProps,
  OnboardingIconProps,
  OnboardingTitleProps,
} from './types';

// =============================================================================
// Content Wrapper
// =============================================================================

/**
 * Content wrapper for dialog body
 */
export const OnboardingContent: React.FC<OnboardingContentProps> = ({
  children,
  className,
}) => {
  return <div className={cn('flex flex-col gap-6', className)}>{children}</div>;
};

// =============================================================================
// Header Components
// =============================================================================

/**
 * Header container - groups Icon, Title, Description
 */
export const OnboardingHeader: React.FC<OnboardingHeaderProps> = ({
  children,
  centered = true,
  className,
}) => {
  return (
    <div
      className={cn(
        'flex flex-col gap-3',
        centered && 'items-center text-center',
        className
      )}
    >
      {children}
    </div>
  );
};

/**
 * Icon wrapper with background circle
 */
export const OnboardingIcon: React.FC<OnboardingIconProps> = ({
  children,
  className,
}) => {
  return (
    <div
      className={cn(
        'flex h-14 w-14 items-center justify-center rounded-full',
        'bg-brand-primary text-brand-primary',
        '[&>svg]:h-7 [&>svg]:w-7',
        className
      )}
    >
      {children}
    </div>
  );
};

/**
 * Title typography
 */
export const OnboardingTitle: React.FC<OnboardingTitleProps> = ({
  children,
  className,
}) => {
  return (
    <Text
      as='h2'
      size={TextSizes.XL}
      weight={FontWeights.SEMIBOLD}
      className={cn('tracking-tight text-primary', className)}
    >
      {children}
    </Text>
  );
};

/**
 * Description/subtitle text
 */
export const OnboardingDescription: React.FC<OnboardingDescriptionProps> = ({
  children,
  className,
}) => {
  return (
    <Text as='p' className={cn('text-tertiary', className)}>
      {children}
    </Text>
  );
};

// =============================================================================
// Body & Footer
// =============================================================================

/**
 * Body/main content area
 */
export const OnboardingBody: React.FC<OnboardingBodyProps> = ({
  children,
  className,
}) => {
  return <div className={cn('flex-1 space-y-4', className)}>{children}</div>;
};

/**
 * Footer with action buttons
 */
export const OnboardingFooter: React.FC<OnboardingFooterProps> = ({
  children,
  className,
}) => {
  return (
    <div
      className={cn(
        'flex flex-col-reverse gap-2 pt-4',
        'sm:flex-row sm:justify-end',
        className
      )}
    >
      {children}
    </div>
  );
};
